/**
 * Venue search / autocomplete module.
 * - Primary: Algolia search by pincode, area, city, name (when configured).
 * - Fallback: Filter provided Firestore venues by query.
 * - When Algolia returns insufficient results: show "Add custom location" via Google Places (optional).
 *   Selecting a place creates a custom venue; it does not overwrite or mix with Algolia results.
 * - Game-dependent: pass gameId; show venues even if no court for that game (mark "No availability for this game").
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchVenues, isAlgoliaConfigured } from '../../services/venueAlgoliaService';
import { sendVenueClickedAfterSearch } from '../../services/algoliaInsightsService';
import AlgoliaLogo from '../AlgoliaLogo';
import PlaceAutocomplete, { isPlacesAvailable } from '../PlaceAutocomplete';
import './VenueSearch.css';

const DEBOUNCE_MS = 300;
/** When Algolia returns fewer than this many hits, show "Add custom location" (Google Places) if available. */
const INSUFFICIENT_RESULTS_THRESHOLD = 5;

function normalizeQuery(q) {
  return (q || '').trim().toLowerCase();
}

function venueMatchesQuery(venue, query) {
  if (!query) return true;
  const n = normalizeQuery;
  const name = n(venue.name || '');
  const address = n(venue.address || '');
  const pincode = n(String(venue.pincode || ''));
  const city = n(venue.city || '');
  const area = n(venue.area || '');
  return (
    name.includes(query) ||
    address.includes(query) ||
    pincode.includes(query) ||
    city.includes(query) ||
    area.includes(query)
  );
}

export default function VenueSearch({
  gameId = '',
  firestoreVenues = [],
  onSelect,
  onSelectCustomPlace,
  placeholder = 'Search by pincode, area, city or venue name...',
  disabled = false,
  className = '',
  insufficientResultsThreshold = INSUFFICIENT_RESULTS_THRESHOLD,
}) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [customPlaceQuery, setCustomPlaceQuery] = useState('');
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const queryIDRef = useRef(null);
  const showCustomLocation = open && (query || '').trim().length > 0 && hits.length < insufficientResultsThreshold && isPlacesAvailable() && typeof onSelectCustomPlace === 'function';

  const runSearch = useCallback(
    async (q) => {
      const trimmed = (q || '').trim();
      setSearchError(null);
      if (isAlgoliaConfigured()) {
        setLoading(true);
        try {
          const result = await searchVenues(trimmed, { gameId: gameId || undefined, hitsPerPage: 20 });
          const list = result.hits || result || [];
          setHits(list);
          queryIDRef.current = result.queryID != null ? result.queryID : null;
        } catch (e) {
          setHits([]);
          queryIDRef.current = null;
          setSearchError(e?.message || 'Search unavailable');
          if (process.env.NODE_ENV === 'development') {
            console.error('Venue search failed:', e);
          }
        } finally {
          setLoading(false);
        }
      } else {
        queryIDRef.current = null;
        const filtered = (firestoreVenues || []).filter((v) => venueMatchesQuery(v, trimmed));
        setHits(
          filtered.map((v) => ({
            objectID: v.id,
            venueId: v.id,
            name: v.name,
            address: v.address,
            pincode: v.pincode,
            city: v.city,
            area: v.area,
            gamesSupported: v.gameIds || [],
            courtCount: (v.courts || []).length,
            courts: (v.courts || []).map((c) => ({ id: c.id, name: c.name, gameIds: c.gameIds || [] })),
            hasGame: gameId ? (v.gameIds || []).some((g) => g === gameId) || (v.courts || []).some((c) => (c.gameIds || []).includes(gameId)) : true,
          }))
        );
      }
    },
    [gameId, firestoreVenues]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setHits([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      runSearch(query);
      setOpen(true);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(hit, positionIndex) {
    const objectID = hit.objectID || hit.venueId;
    if (objectID && queryIDRef.current) {
      sendVenueClickedAfterSearch({
        queryID: queryIDRef.current,
        objectID,
        position: (positionIndex != null ? positionIndex : 0) + 1,
      });
    }
    const venue = {
      id: hit.objectID || hit.venueId,
      name: hit.name,
      address: hit.address,
      pincode: hit.pincode,
      city: hit.city,
      area: hit.area,
      gameIds: hit.gameIds || hit.gamesSupported || [],
      courts: hit.courts || [],
      courtCount: hit.courtCount ?? (hit.courts || []).length,
      availabilityText: hit.availabilityText,
      openNow: hit.openNow,
    };
    setSelectedVenueId(venue.id);
    setQuery(hit.name || '');
    setOpen(false);
    setHits([]);
    onSelect && onSelect(venue, { hasGame: hit.hasGame !== false });
  }

  function clearSelection() {
    setSelectedVenueId(null);
    setQuery('');
    setHits([]);
    onSelect && onSelect(null);
  }

  return (
    <div className={`venue-search ${className}`} ref={wrapperRef}>
      <div className="venue-search-input-wrap">
        <input
          type="text"
          className="venue-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim() && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open && (hits.length > 0 || showCustomLocation)}
          aria-controls="venue-search-listbox"
        />
        {selectedVenueId && (
          <button type="button" className="venue-search-clear" onClick={clearSelection} aria-label="Clear selection">
            ✕
          </button>
        )}
        {loading && <span className="venue-search-spinner" aria-hidden />}
      </div>
      {(open && (hits.length > 0 || showCustomLocation || searchError)) && (
        <div className="venue-search-results-wrap">
          {searchError && (
            <p className="venue-search-error" role="alert">
              Search unavailable. Try again later.
            </p>
          )}
          {hits.length > 0 && (
            <ul id="venue-search-listbox" className="venue-search-dropdown" role="listbox">
              {hits.map((hit, index) => {
                const hasGame = hit.hasGame !== false;
                return (
                  <li
                    key={hit.objectID || hit.venueId}
                    role="option"
                    aria-selected={selectedVenueId === (hit.objectID || hit.venueId)}
                    className="venue-search-hit"
                    onClick={() => handleSelect(hit, index)}
                  >
                    <div className="venue-search-hit-name">
                      {hit.name}
                      {hit.verified && <span className="venue-search-hit-verified" title="Claimed venue">Verified</span>}
                    </div>
                    {(hit.area || hit.city || hit.pincode) && (
                      <div className="venue-search-hit-meta">
                        {[hit.area, hit.city, hit.pincode].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {hit.address && <div className="venue-search-hit-address">{hit.address}</div>}
                    <div className="venue-search-hit-stats">
                      <span className="venue-search-hit-stats-detail">
                        {(hit.courtCount ?? 0)} court(s)
                        {hit.availabilityText && (
                          <span className="venue-search-hit-availability"> · {hit.availabilityText}</span>
                        )}
                        {gameId && !hasGame && (
                          <span className="venue-search-hit-no-game"> · No court for this game</span>
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {showCustomLocation && (
            <div className="venue-search-custom-location">
              <p className="venue-search-custom-location-label">
                {hits.length === 0 ? 'No matching venues?' : 'Or add a custom location:'}
              </p>
              <PlaceAutocomplete
                value={customPlaceQuery}
                onChange={setCustomPlaceQuery}
                onSelect={(place) => {
                  setCustomPlaceQuery(place.formattedAddress || '');
                  setOpen(false);
                  setQuery('');
                  setHits([]);
                  onSelectCustomPlace && onSelectCustomPlace(place);
                }}
                placeholder="Search address or place (Google Maps)"
                className="venue-search-custom-place-input"
              />
            </div>
          )}
          {hits.length > 0 && (
            <div className="venue-search-algolia-footer">
              <AlgoliaLogo compact showText />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
